/* Jane Lushka v1.2: sticky add bar and size sheet (phase 3, pdp:16, M3, L7, L16, L30, L35).
   Markup: snippets/jl-pdp-sticky.liquid. Styles: assets/jl-sticky-atc.css.
   1) Visibility: shown while the inline ADD's bottom is above the header's bottom. Decided from positions on scroll
      (passive, one rAF), resize, load, pageshow, init, a layout change of the info column and every jl:pdp-state,
      so an instant jump down or back up always ends in the right state (no IntersectionObserver).
      Hidden = inert + aria-hidden + translated out. body.jl-atc-visible while shown (Gorgias offset), and
      html scroll-padding-bottom = bar height + gap, so a focused field is never scrolled under the bar, and a
      keyboard focus the browser leaves under the bar (already on screen) is scrolled up above it (fix 3).
      The thumbnail only gets its src (from data-src) the first time the bar shows (fix 34).
   2) State: window.JLPdp (assets/jl-pdp-buybox.js) and the data map #jl-pdp-data-<section id>. jl:pdp-ready and
      jl:pdp-state re-render price, size text, select value, pressed sheet option and the ADD label.
      Without JLPdp the bar reads the checked radios itself.
   3) ADD: needs a size -> phones open the sheet (intent add, or notify when all sizes are sold out), desktop calls
      JLPdp.requireSize('sticky'), shows the visible error and focuses the select; sold out -> JLPdp.openNotify with
      focus and scrolls the notify panel into view; available -> form.requestSubmit(inlineAdd) once product-form is
      defined, so product-form.js stays the only add path (L7). The inline ADD's .loading is mirrored as aria-busy
      (and disabled unless the sticky ADD has focus, so the bag can hand focus back to it). A failed add from the bar
      (jl:pdp-add-error with source sticky, from jl-pdp-buybox.js) shows the message in the bar's error line (fix 7).
   4) Sheet: modal dialog, focus moves inside synchronously (iOS), Tab and Shift+Tab cycle, Escape and the backdrop
      close, focus returns to the opener, body.jl-sheet-open locks the page, drag the handle down to close,
      crossing 768px closes it. While open, the rest of the page is inert (fix 25); only what the sheet set is released.
   Events: jl:pdp-sticky-add { breakpoint, has_size }, jl:pdp-sheet-open { intent } (GA4 in ga4-custom-events). */
(function () {
  'use strict';

  if (window.__jlStickyAtcLoaded) return;
  window.__jlStickyAtcLoaded = true;

  var mqDesktop = window.matchMedia ? window.matchMedia('(min-width: 768px)') : { matches: true };
  var controllers = [];
  var openCtrl = null;

  /* ---------- helpers ---------- */
  function cssEsc(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
  }

  function whenDefined(name) {
    if (!window.customElements || typeof window.customElements.whenDefined !== 'function') return Promise.resolve();
    if (window.customElements.get(name)) return Promise.resolve();
    return window.customElements.whenDefined(name);
  }

  function fill(template, key, value) {
    return String(template || '').split('[' + key + ']').join(value == null ? '' : String(value));
  }

  function visible(el) {
    return !!(el && document.documentElement.contains(el) && el.getClientRects().length);
  }

  function focusEl(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  function headerEl() {
    return document.getElementById('shopify-section-header') ||
      document.querySelector('.shopify-section-group-header-group') ||
      document.querySelector('header');
  }

  // the bottom edge of the header while it sticks to the top; 0 when it scrolls away with the page
  function headerBottom() {
    var h = headerEl();
    if (!h) return 0;
    var pos = window.getComputedStyle(h).position;
    if (pos !== 'sticky' && pos !== '-webkit-sticky' && pos !== 'fixed') return 0;
    var r = h.getBoundingClientRect();
    return Math.max(0, r.bottom);
  }

  /* ---------- per section controller ---------- */
  function Controller(bar) {
    this.bar = bar;
    this.section = bar.getAttribute('data-section-id') || '';
    this.pdp = bar.closest('[data-jl-pdp]') ||
      document.querySelector('[data-jl-pdp][data-section-id="' + cssEsc(this.section) + '"]') ||
      document;
    this.sheet = document.getElementById('jl-size-sheet-' + this.section);
    this.panel = this.sheet ? this.sheet.querySelector('[data-jl-sheet-panel]') : null;
    this.add = bar.querySelector('[data-jl-sticky-add]');
    this.addLabel = bar.querySelector('[data-jl-sticky-add-label]');
    this.select = bar.querySelector('[data-jl-sticky-select]');
    this.selectText = bar.querySelector('[data-jl-sticky-select-text]');
    this.trigger = bar.querySelector('[data-jl-sticky-size-trigger]');
    this.error = bar.querySelector('[data-jl-sticky-error]');
    this.sizeWord = bar.getAttribute('data-size-word') === 'true';
    this.sizeIndex = parseInt(bar.getAttribute('data-size-index'), 10) || 0;
    this.sizeName = bar.getAttribute('data-size-name') || '';
    this.shown = null;
    this.intent = 'select';
    this.opener = null;
    this.watched = null;
    this.data = null;
    this.dataEl = null;
    this.bind();
    this.watchInlineAdd();
  }

  Controller.prototype.alive = function () {
    return document.documentElement.contains(this.bar);
  };

  Controller.prototype.readData = function () {
    var el = document.getElementById('jl-pdp-data-' + this.section);
    if (el && el !== this.dataEl) {
      this.dataEl = el;
      try { this.data = JSON.parse(el.textContent || '{}'); } catch (e) { this.data = null; }
    }
    return this.data || {};
  };

  Controller.prototype.str = function (key, attr) {
    var d = this.readData();
    var s = d.strings && d.strings[key];
    if (typeof s === 'string' && s) return s;
    return this.bar.getAttribute('data-str-' + attr) || '';
  };

  Controller.prototype.inlineAdd = function () {
    return this.pdp.querySelector('.product-form [name="add"]') ||
      this.pdp.querySelector('[data-jl-add]') ||
      document.querySelector('.jl-buy [name="add"]');
  };

  Controller.prototype.sizeRadios = function () {
    var field = this.pdp.querySelector('[data-jl-size-fieldset]');
    if (field) return field.querySelectorAll('input[type="radio"]');
    if (this.sizeName) {
      return this.pdp.querySelectorAll('variant-radios input[type="radio"][name="' + cssEsc(this.sizeName) + '"]');
    }
    return [];
  };

  // JLPdp state, or the same shape read from the radios and the data map
  Controller.prototype.state = function () {
    if (window.JLPdp && typeof window.JLPdp.getState === 'function') {
      try {
        var s = window.JLPdp.getState(this.section);
        if (s) return s;
      } catch (e) { /* fall through */ }
    }
    var d = this.readData();
    var form = this.pdp.querySelector('form[data-type="add-to-cart-form"]');
    var idInput = form ? form.querySelector('[name="id"]') : null;
    var id = idInput && idInput.value ? String(idInput.value) : null;
    var radios = this.sizeRadios();
    var size = null;
    var needsSize = false;
    if (radios.length) {
      for (var i = 0; i < radios.length; i++) if (radios[i].checked) size = radios[i].value;
      needsSize = size === null;
    }
    var v = id && d.variants ? d.variants[id] : null;
    return {
      section: this.section,
      variantId: needsSize ? null : id,
      size: size !== null ? size : (v ? v.size : null),
      available: v ? !!v.available : !!(id && !needsSize),
      needsSize: needsSize,
      soldOutAll: !!d.soldOutAll,
      price: v ? v.price : null,
      low: v ? v.low : '',
      ready: v ? !!v.ready : false
    };
  };

  /* ---------- render ---------- */
  Controller.prototype.render = function (st) {
    st = st || this.state();
    var d = this.readData();
    var v = st.variantId && d.variants ? d.variants[st.variantId] : null;
    var price = (v && v.price) || (typeof st.price === 'string' ? st.price : '');
    var sale = !!(v && v.compare);
    var size = st.size || (v && v.size) || '';
    var i;

    if (price) {
      var prices = this.bar.querySelectorAll('[data-jl-sticky-price]');
      for (i = 0; i < prices.length; i++) {
        if (prices[i].textContent !== price) prices[i].textContent = price;
        prices[i].classList.toggle('jl-sticky__price--sale', sale && prices[i].classList.contains('jl-sticky__price'));
        prices[i].classList.toggle('jl-sticky__meta-price--sale', sale && prices[i].classList.contains('jl-sticky__meta-price'));
      }
    }

    var sizeEl = this.bar.querySelector('[data-jl-sticky-size]');
    if (sizeEl) {
      var meta = st.needsSize || !size ? this.str('stickySelect', 'select') : fill(this.str('sizeShort', 'size-short'), 'size', size);
      if (sizeEl.textContent !== meta) sizeEl.textContent = meta;
    }

    if (this.select) {
      var want = st.needsSize ? '' : size;
      if (this.select.value !== want) {
        var has = false;
        for (i = 0; i < this.select.options.length; i++) if (this.select.options[i].value === want) has = true;
        this.select.value = has ? want : '';
      }
      if (this.selectText) {
        var txt;
        if (!this.select.value) {
          txt = this.select.options.length ? this.select.options[0].text : '';
        } else if (this.sizeWord) {
          txt = fill(this.str('sizeValue', 'size-value'), 'size', this.select.value);
        } else {
          txt = this.select.value;
        }
        if (this.selectText.textContent !== txt) this.selectText.textContent = txt;
      }
    }

    if (this.sheet) {
      var opts = this.sheet.querySelectorAll('[data-jl-sheet-option]');
      for (i = 0; i < opts.length; i++) {
        var pressed = !st.needsSize && opts[i].getAttribute('data-option-value') === size;
        opts[i].setAttribute('aria-pressed', pressed ? 'true' : 'false');
      }
    }

    if (this.addLabel) {
      var notify = st.soldOutAll || (!st.needsSize && st.available === false);
      var label = notify ? this.str('notify', 'notify') : this.str('add', 'add');
      if (label && this.addLabel.textContent !== label) this.addLabel.textContent = label;
    }

    if (!st.needsSize) this.clearError();
  };

  Controller.prototype.showError = function () {
    var msg = this.str('required', 'required');
    if (this.error) {
      this.error.textContent = msg;
      this.bar.classList.add('has-error');
    }
    if (this.select) this.select.setAttribute('aria-invalid', 'true');
  };

  Controller.prototype.clearError = function () {
    if (this.error && this.error.textContent) this.error.textContent = '';
    this.bar.classList.remove('has-error', 'has-add-error');
    if (this.select && this.select.hasAttribute('aria-invalid')) this.select.removeAttribute('aria-invalid');
  };

  // a failed /cart/add from this bar (jl:pdp-add-error from the buy box): visible in the bar at both widths
  Controller.prototype.showAddError = function (message) {
    if (!this.error) return;
    if (!message) {
      if (this.bar.classList.contains('has-add-error')) this.clearError();
      return;
    }
    this.bar.classList.remove('has-error');
    this.bar.classList.add('has-add-error');
    if (this.error.textContent !== message) this.error.textContent = message;
  };

  /* ---------- visibility ---------- */
  Controller.prototype.shouldShow = function () {
    var add = this.inlineAdd();
    if (!add) return false;
    var r = add.getBoundingClientRect();
    if (!r.width && !r.height) return false;
    return r.bottom < headerBottom();
  };

  Controller.prototype.setShown = function (show) {
    if (this.shown === show) return;
    // keep the bar while its sheet is open (the sheet's opener lives in it); closeSheet decides again
    if (!show && this.shown && openCtrl === this) return;
    this.shown = show;
    var bar = this.bar;
    if (show) {
      bar.removeAttribute('inert');
      bar.removeAttribute('aria-hidden');
      bar.classList.add('is-visible');
      // PF3: the thumbnail downloads the first time the bar shows
      var thumb = bar.querySelector('img[data-src]');
      if (thumb && !thumb.getAttribute('src')) thumb.setAttribute('src', thumb.getAttribute('data-src'));
    } else {
      bar.classList.remove('is-visible');
      bar.setAttribute('inert', '');
      bar.setAttribute('aria-hidden', 'true');
    }
    syncBody();
  };

  // AV3: while the bar shows, focus and anchor scrolling stop above it (its height, its inset and a margin)
  function syncBody() {
    var shown = null;
    for (var i = 0; i < controllers.length; i++) if (controllers[i].shown) shown = controllers[i];
    document.body.classList.toggle('jl-atc-visible', !!shown);
    var pad = '';
    if (shown) {
      var inset = parseFloat(window.getComputedStyle(shown.bar).marginBottom) || 0;
      pad = Math.round(shown.bar.offsetHeight + inset + (mqDesktop.matches ? 16 : 24)) + 'px';
    }
    if (document.documentElement.style.scrollPaddingBottom !== pad) document.documentElement.style.scrollPaddingBottom = pad;
  }

  var updateQueued = false;
  var syncQueued = false;
  function updateAll() {
    updateQueued = false;
    for (var i = 0; i < controllers.length; i++) {
      var c = controllers[i];
      if (c.alive()) c.setShown(c.shouldShow());
    }
    // the bar's height can change while it shows (width, an add error): measure again once
    if (syncQueued) {
      syncQueued = false;
      syncBody();
    }
  }
  function queueUpdate() {
    if (updateQueued) return;
    updateQueued = true;
    window.requestAnimationFrame(updateAll);
  }

  /* ---------- loading mirror (L7) ---------- */
  Controller.prototype.watchInlineAdd = function () {
    var add = this.inlineAdd();
    if (!add || add === this.watched || typeof MutationObserver !== 'function') return;
    this.watched = add;
    if (this.mo) this.mo.disconnect();
    var self = this;
    this.mo = new MutationObserver(function () { self.mirrorLoading(); });
    this.mo.observe(add, { attributes: true, attributeFilter: ['class', 'aria-busy'] });
  };

  Controller.prototype.mirrorLoading = function () {
    var add = this.watched;
    var btn = this.add;
    if (!add || !btn) return;
    var busy = add.classList.contains('loading') || add.getAttribute('aria-busy') === 'true';
    if (busy) {
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-loading');
      // a focused button keeps focus (the bag returns focus to it when it closes)
      if (document.activeElement !== btn) btn.disabled = true;
    } else {
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  };

  /* ---------- actions ---------- */
  Controller.prototype.submit = function () {
    var add = this.inlineAdd();
    if (!add) return;
    var form = add.form || add.closest('form');
    if (!form) return;
    whenDefined('product-form').then(function () {
      if (add.classList.contains('loading')) return;
      // the buy box reports a failed add here instead of under the pills (jl:pdp-add-error)
      form.__jlAddSource = 'sticky';
      if (typeof form.requestSubmit === 'function') form.requestSubmit(add);
      else add.click();
      form.__jlAddSource = null;
    });
  };

  Controller.prototype.selectSize = function (value, source) {
    var ok = false;
    if (window.JLPdp && typeof window.JLPdp.selectSize === 'function') {
      try { ok = window.JLPdp.selectSize(value, { source: source }) !== false; } catch (e) { ok = false; }
      if (ok) return true;
    }
    var radios = this.sizeRadios();
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].value === value) {
        if (!radios[i].checked) {
          radios[i].checked = true;
          radios[i].dispatchEvent(new Event('change', { bubbles: true }));
        }
        return true;
      }
    }
    return false;
  };

  Controller.prototype.notify = function (variantId, source) {
    var panel = this.pdp.querySelector('[data-jl-notify]');
    if (window.JLPdp && typeof window.JLPdp.openNotify === 'function') {
      try { window.JLPdp.openNotify(variantId, { focus: true, source: source }); } catch (e) { /* ignore */ }
    }
    if (panel && visible(panel)) {
      try { panel.scrollIntoView({ block: 'center', behavior: 'auto' }); } catch (e) { panel.scrollIntoView(); }
      var email = panel.querySelector('[data-jl-notify-email]');
      if (email && visible(email)) {
        if (document.activeElement !== email) focusEl(email);
      } else {
        // no email field (Amp popup or app fallback): its button, else the chosen size radio (as the inline flow)
        var active = document.activeElement;
        var lost = !active || active === document.body || active === document.documentElement || !visible(active) ||
          !!active.closest('[inert]') || this.bar.contains(active) || (this.sheet && this.sheet.contains(active));
        var popup = panel.querySelector('[data-jl-notify-popup]');
        var radio = this.pdp.querySelector('[data-jl-size-fieldset] input:checked');
        if (popup && visible(popup)) focusEl(popup);
        else if (lost && radio) focusEl(radio);
      }
    }
    queueUpdate();
  };

  // two option lists: the inline pills decide; bring them into view
  Controller.prototype.toPills = function () {
    var size = this.pdp.querySelector('[data-jl-size]');
    if (!size) return;
    try { size.scrollIntoView({ block: 'center', behavior: 'auto' }); } catch (e) { size.scrollIntoView(); }
    var radios = size.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      var fs = radios[i].closest('fieldset');
      if (fs && !fs.querySelector('input:checked')) { focusEl(radios[i]); break; }
    }
  };

  Controller.prototype.onAdd = function () {
    var btn = this.add;
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    var st = this.state();
    var desktop = mqDesktop.matches;
    dispatch('jl:pdp-sticky-add', { breakpoint: desktop ? 'desktop' : 'mobile', has_size: !st.needsSize });

    if (st.needsSize) {
      if (!desktop && this.sheet) {
        this.openSheet(st.soldOutAll ? 'notify' : 'add', btn);
        return;
      }
      if (window.JLPdp && typeof window.JLPdp.requireSize === 'function') {
        try { window.JLPdp.requireSize('sticky'); } catch (e) { /* ignore */ }
      }
      if (this.select && visible(this.select)) {
        this.showError();
        focusEl(this.select);
      } else {
        this.toPills();
      }
      return;
    }

    if (st.available === false || st.soldOutAll) {
      this.notify(st.variantId, 'sticky');
      return;
    }
    this.submit();
  };

  Controller.prototype.onSelect = function () {
    var value = this.select.value;
    if (!value) {
      this.render();
      return;
    }
    this.selectSize(value, 'sticky');
    this.render();
  };

  /* ---------- sheet ---------- */
  Controller.prototype.focusables = function () {
    if (!this.panel) return [];
    var list = this.panel.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
    var out = [];
    for (var i = 0; i < list.length; i++) if (visible(list[i])) out.push(list[i]);
    return out;
  };

  Controller.prototype.openSheet = function (intent, opener) {
    if (!this.sheet) return;
    if (openCtrl && openCtrl !== this) openCtrl.closeSheet(false);
    this.intent = intent || 'select';
    this.opener = opener || document.activeElement;
    this.render();
    this.sheet.hidden = false;
    this.sheet.classList.remove('is-dragging');
    if (this.panel) this.panel.style.removeProperty('--jl-sheet-drag');
    openCtrl = this;
    document.body.classList.add('jl-sheet-open');
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
    this.inertPage(true);

    // focus moves synchronously inside the click handler (iOS keeps the keyboard and VoiceOver in the sheet)
    var target = this.sheet.querySelector('[data-jl-sheet-option][aria-pressed="true"]') ||
      this.sheet.querySelector('[data-jl-sheet-option]') || this.focusables()[0];
    focusEl(target);

    var sheet = this.sheet;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        if (!sheet.hidden) sheet.classList.add('is-open');
      });
    });
    dispatch('jl:pdp-sheet-open', { intent: this.intent });
  };

  Controller.prototype.closeSheet = function (restore) {
    if (!this.sheet || this.sheet.hidden) return;
    this.sheet.classList.remove('is-open', 'is-dragging');
    if (this.panel) this.panel.style.removeProperty('--jl-sheet-drag');
    this.sheet.hidden = true;
    if (openCtrl === this) openCtrl = null;
    document.body.classList.remove('jl-sheet-open');
    this.inertPage(false);
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
    var opener = this.opener;
    this.opener = null;
    if (restore !== false && opener && visible(opener) && !opener.closest('[inert]')) focusEl(opener);
    // the bar may have been kept for the sheet: decide again
    this.shown = null;
    this.setShown(this.shouldShow());
  };

  // AV14: while the sheet is open everything else is inert (each ancestor's siblings up to body), so screen reader
  // swipes stay in the dialog; only what this sets is released again
  var SKIP_INERT = /^(SCRIPT|STYLE|LINK|META|TEMPLATE|NOSCRIPT)$/;
  Controller.prototype.inertPage = function (on) {
    var list = this.inerted || [];
    var i;
    if (!on) {
      for (i = 0; i < list.length; i++) list[i].removeAttribute('inert');
      this.inerted = [];
      return;
    }
    if (list.length) return;
    for (var el = this.sheet; el && el.parentElement && el !== document.body; el = el.parentElement) {
      var kids = el.parentElement.children;
      for (i = 0; i < kids.length; i++) {
        var k = kids[i];
        if (k === el || SKIP_INERT.test(k.tagName) || k.hasAttribute('inert')) continue;
        k.setAttribute('inert', '');
        list.push(k);
      }
    }
    this.inerted = list;
  };

  Controller.prototype.choose = function (option) {
    var value = option.getAttribute('data-option-value');
    var variantId = option.getAttribute('data-variant-id');
    var soldOut = option.classList.contains('is-sold-out');
    var intent = this.intent;
    this.selectSize(value, 'sheet');
    if (soldOut) {
      this.closeSheet(false);
      this.render();
      this.notify(variantId, 'sheet');
      return;
    }
    this.closeSheet(true);
    this.render();
    if (intent === 'add') this.submit();
  };

  Controller.prototype.onSheetKey = function (event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      event.stopPropagation();
      this.closeSheet(true);
      return;
    }
    if (event.key !== 'Tab') return;
    var f = this.focusables();
    if (!f.length) { event.preventDefault(); return; }
    var first = f[0];
    var last = f[f.length - 1];
    var active = document.activeElement;
    if (event.shiftKey) {
      if (active === first || !this.panel.contains(active)) { event.preventDefault(); focusEl(last); }
    } else if (active === last || !this.panel.contains(active)) {
      event.preventDefault();
      focusEl(first);
    }
  };

  // drag the handle (or the head) down to close
  Controller.prototype.bindDrag = function () {
    var self = this;
    var handle = this.sheet && this.sheet.querySelector('[data-jl-sheet-handle]');
    if (!handle || !this.panel || typeof window.PointerEvent !== 'function') return;
    var startY = null;
    var delta = 0;
    function move(e) {
      if (startY === null) return;
      delta = Math.max(0, e.clientY - startY);
      self.panel.style.setProperty('--jl-sheet-drag', delta + 'px');
    }
    function end() {
      if (startY === null) return;
      startY = null;
      self.sheet.classList.remove('is-dragging');
      if (delta > Math.min(120, self.panel.offsetHeight / 3)) {
        self.closeSheet(true);
      } else {
        self.panel.style.removeProperty('--jl-sheet-drag');
      }
      delta = 0;
    }
    handle.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) return;
      startY = e.clientY;
      delta = 0;
      self.sheet.classList.add('is-dragging');
      try { handle.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    });
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  };

  Controller.prototype.bind = function () {
    var self = this;
    this.bar.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-jl-sticky-add]')) {
        e.preventDefault();
        self.onAdd();
      } else if (t.closest('[data-jl-sticky-size-trigger]')) {
        e.preventDefault();
        self.openSheet('select', t.closest('[data-jl-sticky-size-trigger]'));
      }
    });
    if (this.select) {
      this.select.addEventListener('change', function () { self.onSelect(); });
    }
    if (this.sheet) {
      this.sheet.addEventListener('click', function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        var opt = t.closest('[data-jl-sheet-option]');
        if (opt) {
          self.choose(opt);
          return;
        }
        if (t.closest('[data-jl-sheet-close]')) self.closeSheet(true);
      });
      this.sheet.addEventListener('keydown', function (e) { self.onSheetKey(e); });
      this.bindDrag();
    }
  };

  /* ---------- global wiring ---------- */
  function forSection(section, fn) {
    for (var i = 0; i < controllers.length; i++) {
      var c = controllers[i];
      if (!c.alive()) continue;
      if (!section || c.section === section) fn(c);
    }
  }

  function onState(event) {
    var st = event && event.detail && event.detail.state;
    forSection(st && st.section, function (c) {
      c.watchInlineAdd();
      c.render(st && (!st.section || st.section === c.section) ? st : null);
    });
    queueUpdate();
  }

  function init() {
    // drop controllers whose section was re-rendered or removed
    controllers = controllers.filter(function (c) {
      if (c.alive()) return true;
      if (c.mo) c.mo.disconnect();
      if (openCtrl === c) {
        openCtrl = null;
        document.body.classList.remove('jl-sheet-open');
      }
      return false;
    });
    var bars = document.querySelectorAll('[data-jl-sticky]');
    for (var i = 0; i < bars.length; i++) {
      if (bars[i].__jlSticky && bars[i].__jlSticky.alive()) continue;
      var c = new Controller(bars[i]);
      bars[i].__jlSticky = c;
      controllers.push(c);
    }
    forSection(null, function (c) {
      c.watchInlineAdd();
      c.render();
      c.shown = null;
      c.setShown(c.shouldShow());
    });
    syncBody();
    observeLayout();
  }

  // the inline ADD moves when the buy box changes height (notify panel, colour row, fonts)
  var ro = null;
  function observeLayout() {
    if (typeof window.ResizeObserver !== 'function') return;
    if (!ro) ro = new ResizeObserver(queueUpdate);
    var infos = document.querySelectorAll('[data-jl-pdp] .jl-pdp__info');
    for (var i = 0; i < infos.length; i++) {
      if (infos[i].__jlStickyObserved) continue;
      infos[i].__jlStickyObserved = true;
      ro.observe(infos[i]);
    }
  }

  window.addEventListener('scroll', queueUpdate, { passive: true });
  window.addEventListener('resize', function () {
    syncQueued = true;
    queueUpdate();
  }, { passive: true });
  window.addEventListener('load', function () {
    forSection(null, function (c) { c.watchInlineAdd(); });
    queueUpdate();
  });
  window.addEventListener('pageshow', function () {
    forSection(null, function (c) { c.shown = null; });
    queueUpdate();
  });

  document.addEventListener('jl:pdp-ready', onState);
  document.addEventListener('jl:pdp-state', onState);
  document.addEventListener('jl:pdp-add-error', function (event) {
    var d = (event && event.detail) || {};
    forSection(d.section, function (c) {
      if (!d.message || d.source === 'sticky') c.showAddError(d.message || '');
    });
    syncQueued = true;
    queueUpdate();
  });

  // Escape anywhere while the sheet is open; focus that leaves the sheet is pulled back in
  document.addEventListener('keydown', function (e) {
    if (!openCtrl || (e.key !== 'Escape' && e.key !== 'Esc')) return;
    if (openCtrl.sheet.contains(e.target)) return;
    e.preventDefault();
    openCtrl.closeSheet(true);
  });
  document.addEventListener('focusin', function (e) {
    if (!openCtrl || !openCtrl.panel) return;
    if (openCtrl.sheet.contains(e.target)) return;
    var f = openCtrl.focusables();
    if (f.length) focusEl(f[0]);
  });

  // AV3: scroll-padding only shapes focus moves that scroll while the bar shows. A browser does not scroll for an
  // element that is already on screen, and a focus scroll can itself bring the bar in over the element, so once the
  // page has stopped moving, a keyboard focus that the bar (or the gap above it) covers is scrolled up above it
  function uncover(t) {
    var pad = parseFloat(document.documentElement.style.scrollPaddingBottom) || 0;
    if (!pad) return;
    for (var i = 0; i < controllers.length; i++) {
      var c = controllers[i];
      if (!c.shown) continue;
      if (c.bar.contains(t)) return;
      var r = t.getBoundingClientRect();
      var b = c.bar.getBoundingClientRect();
      var limit = window.innerHeight - pad;
      if (!r.height || r.bottom <= limit || r.top >= window.innerHeight || r.right <= b.left || r.left >= b.right) return;
      var dy = Math.min(r.bottom - limit, r.top - headerBottom());
      if (dy > 0) window.scrollBy(0, dy);
      return;
    }
  }
  // checked once the page has stopped moving: 150 ms without a scroll event after the focus (a smooth focus scroll
  // re-arms the timer with every step)
  var focusPending = null;
  var focusTimer = 0;
  function checkFocus() {
    focusTimer = 0;
    var t = focusPending;
    focusPending = null;
    // (never for the shared quick add sheet, phase 4: a fixed layer, nothing to scroll into view)
    if (t && !openCtrl && document.activeElement === t && !(t.closest && t.closest('[data-jl-qa-sheet]'))) uncover(t);
  }
  function armFocus() {
    if (!focusPending) return;
    window.clearTimeout(focusTimer);
    focusTimer = window.setTimeout(checkFocus, 150);
  }
  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (openCtrl || !t || t === document.body || typeof t.getBoundingClientRect !== 'function') return;
    if (t.closest && t.closest('[data-jl-qa-sheet]')) return;
    try { if (!t.matches(':focus-visible')) return; } catch (err) { /* no :focus-visible: check every focus */ }
    focusPending = t;
    armFocus();
  });
  window.addEventListener('scroll', armFocus, { passive: true });

  function onBreakpoint() {
    if (openCtrl) openCtrl.closeSheet(true);
    queueUpdate();
  }
  if (typeof mqDesktop.addEventListener === 'function') mqDesktop.addEventListener('change', onBreakpoint);
  else if (typeof mqDesktop.addListener === 'function') mqDesktop.addListener(onBreakpoint);

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function () { window.setTimeout(init, 0); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // JLPdp may already be there (scripts loaded late): render from it once it exists
  if (window.JLPdp) forSection(null, function (c) { c.render(); });
})();
